<template>
    <component
        :aria-label="ariaLabel"
        :title="ariaLabel"
        :is="actionType === 'link' ? 'RouterLink' : 'button'"
        :id="id"
        :type="actionType === 'action' ? type : undefined"
        :class="['action-icon-button', isRounded && 'rounded', buttonStyleClass, buttonSizeClass]"
        v-bind="{
            ...componentProps,
            ...$attrs,
            ...(actionType === 'action' ? { onClick: emitClick } : {}),
        }"
        :disabled="disabled"
    >
        <img v-if="imageSrc" class="image" :src="imageSrc" :alt="ariaLabel || ''" />
        <i v-else :class="['icon', icon, iconColorClass, iconClass ? iconClass : '']"></i>
    </component>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { RouterLink } from "vue-router";

import type { ButtonActionType, ButtonStyleType, ButtonSize } from "@/types/buttons";

export default defineComponent({
    components: {
        RouterLink,
    },
    name: "ActionIconButton",
    inheritAttrs: false,

    emits: ["click"],
    props: {
        actionType: {
            type: String as PropType<ButtonActionType>,
            default: "action",
        },
        styleType: {
            type: String as PropType<ButtonStyleType>,
            default: "neutral-outlined",
        },
        type: {
            type: String as PropType<"button" | "submit" | "reset">,
            default: "button",
        },
        size: {
            type: String as PropType<ButtonSize>,
            default: "lg",
        },
        icon: {
            type: String,
            default: "fas fa-question",
        },
        iconClass: {
            type: String,
            default: "",
        },
        imageSrc: {
            type: String,
            default: "",
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        to: {
            type: String,
            default: "",
        },
        isExternal: {
            type: Boolean,
            default: false,
        },
        isRounded: {
            type: Boolean,
            default: false,
        },
        id: String,
        ariaLabel: String,
    },

    computed: {
        buttonStyleClass(): string {
            return `style-${this.styleType}`;
        },

        buttonSizeClass(): string {
            switch (this.size) {
                case "xs":
                    return "size-xs";
                case "sm":
                    return "size-sm";
                case "md":
                    return "size-md";
                case "lg":
                    return "size-lg";
                case "xl":
                    return "size-xl";
                case "xxl":
                    return "size-xxl";
                default:
                    return "size-lg";
            }
        },

        iconColorClass(): string {
            switch (this.styleType) {
                case "primary-brand-filled":
                case "secondary-brand-filled":
                case "neutral-filled":
                case "delete-filled":
                    return "icon-on-filled";
                case "primary-brand-soft":
                    return "icon-primary-brand-soft";
                case "primary-brand-transparent":
                    return "icon-primary-brand";
                case "delete-soft":
                case "delete-outlined":
                case "delete-transparent":
                    return "icon-delete";
                default:
                    return "icon-default";
            }
        },

        componentProps(): Record<string, any> {
            if (this.actionType === "link") {
                return {
                    to: this.to,
                    target: this.isExternal ? "_blank" : "_self",
                    rel: this.isExternal ? "noopener noreferrer" : undefined,
                };
            }
            return {};
        },
    },

    methods: {
        emitClick: function () {
            if (!this.disabled) {
                this.$emit("click");
            }
        },
    },
});
</script>

<style scoped>
@import "@/style/components/buttons.css";
</style>
